import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getConversations, getConfig } from '../../hooks/useAI';
import AIChat from '../../components/ai/AIChat';
import ConversationSidebar from '../../components/ai/ConversationSidebar';

const quickActions = [
  {
    title: 'Generate Email',
    description: 'Write a complete email with AI',
    prompt: 'Help me write a professional email campaign. Ask me about the topic, audience, and goal.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: 'Generate Subject Lines',
    description: 'Get 5+ subject line options',
    prompt: 'I need compelling subject lines for my email campaign. Ask me about the email content.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>
    ),
  },
  {
    title: 'Create Workflow',
    description: 'Build an automation workflow',
    prompt: 'Help me create an email automation workflow. Ask me about the trigger and steps.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    title: 'Suggest Segment',
    description: 'Get segmentation recommendations',
    prompt: 'Help me create contact segments based on engagement and behavior. Ask me about my contacts.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
];

export default function AIPage() {
  const navigate = useNavigate();
  const [showChat, setShowChat] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState('');
  const [sidebarRefresh, setSidebarRefresh] = useState(0);

  const { data: configData } = useQuery({
    queryKey: ['ai-config'],
    queryFn: getConfig,
  });

  const { data: conversationsData } = useQuery({
    queryKey: ['ai-conversations'],
    queryFn: () => getConversations(1, 5),
  });

  const handleQuickAction = (prompt: string) => {
    setInitialPrompt(prompt);
    setShowChat(true);
  };

  const handleNewChat = () => {
    setInitialPrompt('');
    setShowChat(true);
    setSidebarRefresh((r) => r + 1);
  };

  const handleSelectConversation = (_id: string) => {
    // TODO: Load specific conversation
    setShowChat(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      {!showChat && (
        <ConversationSidebar
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
          refreshKey={sidebarRefresh}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ai')}
              className="text-sm font-medium text-gray-900 hover:text-blue-600"
            >
              Chat
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => navigate('/ai/workflows')}
              className="text-sm text-gray-500 hover:text-blue-600"
            >
              Workflows
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => navigate('/ai/optimizer')}
              className="text-sm text-gray-500 hover:text-blue-600"
            >
              Optimizer
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => navigate('/ai/business')}
              className="text-sm text-gray-500 hover:text-blue-600"
            >
              Business
            </button>
          </div>
          <div className="flex items-center gap-3">
            {showChat && (
              <button
                onClick={() => setShowChat(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                VSENDS AI
              </h1>
              {configData && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {configData.provider === 'vsends' ? 'VSENDS Built-in' : configData.provider}
                  {configData.model_name && ` · ${configData.model_name}`}
                  {configData.auto_execute && ' · Auto-execute ON'}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/settings/ai')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            AI Settings
          </button>
        </header>

        {showChat ? (
          <AIChat
            initialMessage={initialPrompt}
            taskType="chat"
          />
        ) : (
          <main className="flex-1 p-8 overflow-y-auto">
            <div className="max-w-4xl mx-auto">
              {/* Welcome */}
              <div className="text-center mb-12">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">What can I help you with?</h2>
                <p className="text-gray-500 mt-2">Choose a quick action or start a conversation</p>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
                {quickActions.map((action) => (
                  <button
                    key={action.title}
                    onClick={() => handleQuickAction(action.prompt)}
                    className="flex items-start gap-4 p-5 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all text-left"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                      {action.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{action.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{action.description}</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Recent Conversations */}
              {conversationsData?.items && conversationsData.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">Recent Conversations</h3>
                  <div className="space-y-2">
                    {conversationsData.items.slice(0, 5).map((convo) => (
                      <button
                        key={convo.id}
                        onClick={() => handleSelectConversation(convo.id)}
                        className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{convo.title || 'Untitled'}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {convo.provider_used === 'vsends' ? 'VSENDS AI' : convo.provider_used} · {convo.message_count} messages
                          </p>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
